import { ReactElement } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { Appearance, Placement } from '../../../Components/designConstants';
import { IconButton } from '../../../Components/IconButton';
import { InfoTooltip } from '../../../Components/InfoTooltip';
import { downloadPNG } from '../../../utils/download-utils';
import { buildYAxisViews } from './scaling-chart-utils';
import { ModelMetricsPanel } from './ModelMetricsPanel';
import { SaturationCurveChart } from './SaturationCurveChart';
import { ScalingAnalysisTable } from './ScalingAnalysisTable';
import { ScalingDecisionRead } from './ScalingDecisionRead';
import { ScalingSliderCard } from './ScalingSliderCard';
import { ScalingOutcomeData } from './scalingData';
import './ScalingAnalysisTab.scss';

interface ScalingAnalysisTabProps {
  channel: string;
  platform: string;
  tactic: string;
  kpi: string;
  // The selected outcome drives both y-axis views: the value curve and its efficiency metric.
  outcomeData: ScalingOutcomeData;
}

interface QuestionHeaderProps {
  channel: string;
  combo: string;
  outcomeWord: string;
}

const QuestionHeader = ({ channel, combo, outcomeWord }: QuestionHeaderProps): ReactElement => (
  <div className='scaling-question-header'>
    <div className='scaling-question-title'>How far can you push {channel} performance?</div>
    <div className='scaling-question-subtitle'>
      <span>
        {combo} — the saturation curve shows where additional spend stops driving proportional{' '}
        {outcomeWord}.
      </span>
      <InfoTooltip rightLabel='More info' placement={Placement.BOTTOM}>
        The curve fits a power-law model (fit = a · spend<sup>b</sup>) to historical weekly spend
        vs. {outcomeWord}. The solid line covers observed spend; the dotted line extrapolates beyond
        your current max. The shaded band is the 95% prediction interval. Read it to gauge how much
        more {outcomeWord} additional spend is likely to produce — and where returns flatten.
      </InfoTooltip>
    </div>
  </div>
);

export const ScalingAnalysisTab = ({
  channel,
  platform,
  tactic,
  kpi,
  outcomeData
}: ScalingAnalysisTabProps): ReactElement => {
  const isRevenue = outcomeData.efficiencyMetric === 'ROAS';
  const outcomeWord = isRevenue ? 'revenue' : 'volume';
  const combo = `${platform}/${tactic} ${kpi}`;
  const views = buildYAxisViews(outcomeData);
  // Sanitized combo slug → a valid element id for the PNG capture target + the download filename.
  const slug = `${channel}_${platform}_${tactic}_${kpi}`.replace(/[^a-z0-9_]+/gi, '_');
  const chartId = `scaling-chart-${slug}`;
  const title = (
    <>
      Scaling Curve:{' '}
      <span className='scaling-curve-title-detail'>
        {channel}/{platform}/{tactic} {kpi}
      </span>
    </>
  );

  return (
    <div className='scaling-analysis-tab'>
      <QuestionHeader channel={channel} combo={combo} outcomeWord={outcomeWord} />
      <div className='scaling-analysis-body'>
        {/* Plot and table share the left column so the table lines up with the plot's width. */}
        <div className='scaling-main-column'>
          {/* Interlinked spend / outcome / efficiency sliders (reuse the Budget Builder's
              LinkedSlider). Keyed on the selection so it re-anchors to current spend on change. */}
          <ScalingSliderCard
            key={`${platform}/${tactic}/${kpi}/${outcomeData.efficiencyMetric}`}
            outcomeData={outcomeData}
            kpi={kpi}
          />
          {/* Lead with the marginal-return read (break-even + next-dollar ROAS) for revenue outcomes;
              a volume curve has no 1x break-even, so it's ROAS-only. */}
          {isRevenue && (
            <ScalingDecisionRead curve={outcomeData.curve} params={outcomeData.params} />
          )}
          <div className='scaling-plot'>
            {/* Head row holds the plot controls; download sits right (scope D's plot-view
                toggle will land on the left here, matching the Budget Builder's plot head). */}
            <div className='scaling-plot-head'>
              <IconButton
                appearance={Appearance.OUTLINED}
                onClick={() => downloadPNG(`#${chartId}`, `scaling_${slug}`)}
                title='Download plot as PNG'
              >
                <MdOutlineFileDownload />
              </IconButton>
            </div>
            <div id={chartId} className='scaling-plot-chart'>
              <SaturationCurveChart views={views} title={title} />
            </div>
          </div>
          <ScalingAnalysisTable
            curve={outcomeData.curve}
            params={outcomeData.params}
            efficiencyMetric={outcomeData.efficiencyMetric}
            channel={channel}
            platform={platform}
            tactic={tactic}
            kpi={kpi}
          />
        </div>
        <ModelMetricsPanel
          metrics={outcomeData.metrics}
          extrapPoint={outcomeData.extrapPoint}
          efficiencyMetric={outcomeData.efficiencyMetric}
        />
      </div>
    </div>
  );
};
